<script lang="ts" setup>
import { useTimeoutFn } from '@vueuse/core';

const props = withDefaults(
  defineProps<{
    color?: 'inherit' | 'on-fill' | 'primary' | 'info' | 'error';
    showText?: boolean;
    loading?: boolean;
  }>(),
  {
    color: 'inherit',
  },
);

const width = computed(() => (props.showText ? 176 : 35));

// The filled bookmark shape (outer boundary + inner hollow).
const FILL_PATH =
  'M32.2792 0.102647C32.6593 -0.0630805 33.0973 -0.026561 33.4441 0.200844C33.7909 0.428269 34 0.815598 34 1.2306V28.6641C34 29.0791 33.7908 29.4664 33.4441 29.6938C33.0972 29.9212 32.6585 29.9579 32.2783 29.792L17 23.1243L1.72696 29.8947C1.34673 30.0631 0.907062 30.0279 0.558565 29.8009C0.210051 29.5738 7.03897e-05 29.1856 0 28.7694V1.2306C0 0.815559 0.209065 0.428259 0.555914 0.200844C0.902718 -0.0264088 1.34077 -0.0631271 1.72077 0.102647L16.9973 6.76595L32.2792 0.102647ZM17.4887 9.23771C17.1755 9.37431 16.8192 9.37434 16.506 9.23771L2.45875 3.10874V26.8779L16.4998 20.6552L16.6191 20.6092C16.903 20.5173 17.2125 20.532 17.4887 20.6526L31.5404 26.7851V3.10874L17.4887 9.23771Z';

// Loading animation: a single CSS conic-gradient (inherently smooth — no segments,
// no blur) is rotated behind a mask of the bookmark shape, so the sweep only shows
// inside the frame. We rotate the element itself (transform) rather than animating
// the gradient's angle, so no @property registration is needed — the effect works
// in every browser that can paint a conic-gradient (same capability the dim is
// gated on below). The mask is a data-URI of FILL_PATH.
const maskUrl = computed(() => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width.value} 30"><path d="${FILL_PATH}" fill="#000"/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
});

// The sweep colour mirrors the static logo fill, so it's white when
// `color="inherit"` (e.g. login) and the brand accent for `color="primary"`
// (e.g. the loading-organisation screen). Uses the accent-colour tokens
// (intended for icons / small UI), never a raw colour.
const sweepColor = computed(() => {
  switch (props.color) {
    case 'on-fill':
      return 'var(--accent-color-on-fill)';
    case 'primary':
      return 'var(--accent-color-primary)';
    case 'info':
      return 'var(--accent-color-info)';
    case 'error':
      return 'var(--accent-color-error)';
    default:
      return 'currentColor';
  }
});

// Minimum on-screen time for the loader. Without this, a fast response (or a quick
// error) makes the loader fade in then straight back out — a messy sub-second
// flash. Once shown, it holds for at least this long so the in/hold/out always
// reads cleanly. `showLoader` (not the raw `loading` prop) drives the visuals.
const MIN_VISIBLE_MS = 550;
// oxlint-disable-next-line local-rules/no-setup-props-reactivity-loss
const showLoader = ref(props.loading);
// Seed so a logo that mounts already-loading (e.g. AppLoadingScreen) still gets
// the minimum-display guarantee on its first hide.
// oxlint-disable-next-line local-rules/no-setup-props-reactivity-loss
let shownAt = props.loading ? Date.now() : 0;

const hideDelay = ref(0);
const { start: scheduleHide, stop: cancelHide } = useTimeoutFn(
  () => {
    showLoader.value = false;
  },
  hideDelay,
  { immediate: false },
);

watch(
  () => props.loading,
  (isLoading) => {
    cancelHide();
    if (isLoading) {
      shownAt = Date.now();
      showLoader.value = true;
    } else {
      const remaining = Math.max(0, MIN_VISIBLE_MS - (Date.now() - shownAt));
      if (remaining === 0) showLoader.value = false;
      else {
        hideDelay.value = remaining;
        scheduleHide();
      }
    }
  },
);
</script>

<template>
  <svg :width height="30" :viewBox="`0 0 ${width} 30`" xmlns="http://www.w3.org/2000/svg">
    <g
      class="transition-colors"
      :class="{
        'fill-current': color === 'inherit',
        'fill-on-fill': color === 'on-fill',
        'fill-primary-muted': color === 'primary',
        'fill-info-muted': color === 'info',
        'fill-error-muted': color === 'error',
      }"
    >
      <!-- White brand mark: full opacity when idle, fades to a faint ghost while
           loading so the green trail reads clearly. -->
      <path
        class="logo-fill transition-opacity duration-150"
        :class="{ 'is-loading': showLoader }"
        :d="FILL_PATH"
      />
      <!-- Loading sweep: a smooth conic-gradient hosted in a foreignObject and
           masked to the bookmark shape. Fades in/out over 150ms. -->
      <Transition name="trace-fade">
        <foreignObject v-if="showLoader" x="0" y="0" :width="width" height="30">
          <div
            xmlns="http://www.w3.org/1999/xhtml"
            class="conic-mask"
            :style="{ maskImage: maskUrl, WebkitMaskImage: maskUrl }"
          >
            <div class="conic-sweep" :style="{ '--sweep-color': sweepColor }"></div>
          </div>
        </foreignObject>
      </Transition>

      <g v-if="showText">
        <path
          d="M50 23.2629V6.63104H56.4719C59.7054 6.63104 62.0138 8.8715 62.0138 11.9843C62.0138 14.2724 60.7404 16.0839 58.7802 16.8752L62.4096 23.2629H59.2476L55.89 17.3328H52.8282V23.2629H50.0095H50ZM52.8186 14.7062H56.1523C57.9647 14.7062 59.1665 13.648 59.1665 11.9843C59.1665 10.3206 57.9647 9.26239 56.1523 9.26239H52.8186V14.711V14.7062Z"
        />
        <path
          d="M64.146 7.90826C64.146 7.01684 64.8089 6.36377 65.7151 6.36377C66.6212 6.36377 67.2842 7.01684 67.2842 7.90826C67.2842 8.79967 66.6212 9.42891 65.7151 9.42891C64.8089 9.42891 64.146 8.77584 64.146 7.90826ZM64.3415 23.2625V11.3071H67.0648V23.2625H64.3415Z"
        />
        <path
          d="M69.1958 19.5017H71.9429C72.1146 20.66 73.0971 21.3322 74.4658 21.3322C75.8346 21.3322 76.6978 20.8269 76.6978 19.8878C76.6978 18.9487 76.0111 18.6341 74.3704 18.3671L73.2449 18.1765C70.5741 17.7188 69.4963 16.6796 69.4963 14.6823C69.4963 12.4657 71.5566 11.069 74.2512 11.069C77.1462 11.069 79.2017 12.828 79.278 14.9969H76.5309C76.4069 14.0817 75.6009 13.2379 74.2274 13.2379C73.0494 13.2379 72.167 13.7432 72.167 14.6823C72.167 15.6214 72.9015 15.936 74.5183 16.203L75.6438 16.3936C78.3146 16.8513 79.4211 17.9381 79.4211 19.8878C79.4211 22.1282 77.3608 23.5011 74.4706 23.5011C71.3801 23.5011 69.3484 21.766 69.2006 19.5017H69.1958Z"
        />
        <path
          d="M81.6544 23.2629V6.63104H84.3777V17.2375L89.6715 11.3074H92.9337L88.422 16.3937L93.2008 23.2629H89.9911L86.6335 18.3958L84.3777 20.8079V23.2676H81.6544V23.2629Z"
        />
        <path d="M100.606 23.2629V6.63104H103.425V20.6125H110.412V23.2629H100.606Z" />
        <path
          d="M111.051 17.2852C111.051 13.791 113.774 11.0643 117.227 11.0643C120.68 11.0643 123.308 13.6671 123.308 17.2089C123.308 17.4997 123.308 17.8095 123.26 18.1003H113.75C114.07 20.0548 115.443 21.1607 117.356 21.1607C118.877 21.1607 120.026 20.4123 120.565 19.2825H123.36C122.43 21.8853 120.05 23.5013 117.356 23.5013C113.779 23.5013 111.055 20.7555 111.055 17.2804L111.051 17.2852ZM113.822 16.1554H120.541C120.322 14.5394 119.044 13.3858 117.208 13.3858C115.515 13.3858 114.217 14.4203 113.826 16.1554H113.822Z"
        />
        <path
          d="M124.954 17.2851C124.954 13.791 127.601 11.0643 131.082 11.0643C132.723 11.0643 134.12 11.6888 135.031 12.7756V6.63104H137.754V23.2629H135.155V21.6707C134.273 22.8291 132.804 23.5012 131.087 23.5012C127.605 23.5012 124.958 20.7793 124.958 17.2804L124.954 17.2851ZM135.05 17.2851C135.05 15.14 133.505 13.5717 131.373 13.5717C129.241 13.5717 127.672 15.14 127.672 17.2851C127.672 19.4303 129.241 20.9747 131.373 20.9747C133.505 20.9747 135.05 19.4303 135.05 17.2851Z"
        />
        <path
          d="M141.551 27.9868V25.5747H147.556C149.025 25.5747 149.86 24.8978 149.86 23.2866V21.5276C148.93 22.5906 147.508 23.1913 145.911 23.1913C142.358 23.1913 139.782 20.5409 139.782 17.1182C139.782 13.6956 142.553 11.069 145.934 11.069C147.527 11.069 148.972 11.6458 149.907 12.7803V11.3121H152.506V23.1722C152.506 26.5234 150.351 27.9916 147.48 27.9916H141.547L141.551 27.9868ZM149.788 17.1182C149.788 15.0446 148.243 13.5287 146.135 13.5287C144.027 13.5287 142.505 15.0494 142.505 17.1182C142.505 19.1871 144.027 20.7077 146.135 20.7077C148.243 20.7077 149.788 19.1871 149.788 17.1182Z"
        />
        <path
          d="M154.524 17.2852C154.524 13.791 157.247 11.0643 160.7 11.0643C164.153 11.0643 166.781 13.6671 166.781 17.2089C166.781 17.4997 166.781 17.8095 166.733 18.1003H157.223C157.543 20.0548 158.916 21.1607 160.829 21.1607C162.35 21.1607 163.499 20.4123 164.038 19.2825H166.833C165.903 21.8853 163.523 23.5013 160.829 23.5013C157.252 23.5013 154.528 20.7555 154.528 17.2804L154.524 17.2852ZM157.29 16.1554H164.01C163.79 14.5394 162.512 13.3858 160.676 13.3858C158.983 13.3858 157.686 14.4203 157.295 16.1554H157.29Z"
        />
        <path
          d="M169.013 23.2627V11.3073H171.612V12.9709C172.299 11.9365 173.448 11.3073 175.165 11.3073H176V13.9815H174.846C172.638 13.9815 171.732 15.1637 171.732 17.018V23.2627H169.008H169.013Z"
        />
      </g>
    </g>
  </svg>
</template>

<style scoped>
/* Whole trail fades in when loading starts and out when it ends. */
.trace-fade-enter-active,
.trace-fade-leave-active {
  transition: opacity 150ms ease;
}
.trace-fade-enter-from,
.trace-fade-leave-to {
  opacity: 0;
}

/* Only dim the logo while loading if the browser can paint the sweep. Because the
   sweep is animated via transform (not @property), conic-gradient support is the
   only capability the effect depends on — so this guard is sufficient. */
@supports (background: conic-gradient(from 0deg, red, blue)) {
  .logo-fill.is-loading {
    opacity: 0.15;
  }
}

/* Window onto the rotating gradient, clipped to the bookmark outline. */
.conic-mask {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  -webkit-mask-size: 100% 100%;
  mask-size: 100% 100%;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
}

.conic-sweep {
  position: absolute;
  top: 50%;
  left: 50%;
  /* Oversized and centred so the rotating square always covers the masked logo —
     no uncovered corners at any angle. */
  width: 200%;
  height: 200%;
  /* Bright head at the leading angle, fading smoothly over the 150° behind it.
     --sweep-color mirrors the logo fill (white on login, green on the loading
     screen). */
  background: conic-gradient(
    from 0deg,
    transparent 0deg,
    transparent 210deg,
    var(--sweep-color) 360deg
  );
  /* fast loop; very subtle ease in and out, close to linear */
  animation: logo-spin 0.9s cubic-bezier(0.45, 0.2, 0.55, 0.75) infinite;
}

@keyframes logo-spin {
  from {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

/* Honour reduced-motion: drop the spinning sweep and instead pulse the mark's
   opacity — feedback without rotational motion. */
@media (prefers-reduced-motion: reduce) {
  .conic-mask {
    display: none;
  }
  .logo-fill.is-loading {
    opacity: 1;
    animation: logo-pulse 1.6s ease-in-out infinite;
  }
}

@keyframes logo-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}
</style>
